import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme.js";
import config from "@/config";

import {
  login,
  checkSession,
  addToast,
  logout,
  updatePassword,
} from "../store/app/action.js";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton, SecondaryButton } from "../components/Buttons.jsx";
import { forgotPassword } from "../api/index.js";
import { isValidInput } from "../utils/Utils.js";
import PasswordStrengthIndicator from "../components/PasswordStrengthIndicator.jsx";
import MFACodeModal from "../components/MFACodeModal.jsx";
import MFAMethodSelectionModal from "../components/MFAMethodSelectionModal.jsx";
import { App as CapacitorApp } from "@capacitor/app";

function Signin() {
  const location = useLocation();
  const resetToken = new URLSearchParams(location.search).get("reset");
  const { isApp, isDirect } = useTheme();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.app);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordOk, setPasswordOk] = useState(false);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [mfaMethodSelectionOpen, setMfaMethodSelectionOpen] = useState(false);
  const [mfaCredentials, setMfaCredentials] = useState(null);
  const [mfaOffer, setMfaOffer] = useState(null);
  const [selectedMfaMethod, setSelectedMfaMethod] = useState(null);
  const emailRef = useRef();
  const passwordRef = useRef();
  const newPasswordRef = useRef();
  const confirmPasswordRef = useRef();
  const [appVersion, setAppVersion] = useState(null);

  useEffect(() => {
    CapacitorApp.getInfo()
      .then((info) => {
        setAppVersion(`${info.version} ${info.build ? `(${info.build})` : ""}`);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (resetToken) dispatch(logout(true));
  }, [resetToken]);
  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    if (localStorage.getItem("token")) {
      (async () => {
        setLoading(true);
        try {
          await dispatch(checkSession());
        } catch (e) {
          // Show specific error message if available, otherwise show generic message
          setError(e.message || "Session expired. Please log in again.");
        } finally {
          setLoading(false);
        }
      })();
    }
  }, []);

  const _login = async (e) => {
    setError(null);
    if (e) {
      e.preventDefault();
    }

    if (!resetToken) {
      const email = emailRef.current.value;
      const password = passwordRef.current.value;

      try {
        setLoading(true);

        const result = await dispatch(login(email, password));

        // Check if MFA is required
        if (result && result.requiresMFA) {
          setMfaCredentials({ email, password });
          setMfaOffer(result.offer);

          // If only one MFA method is offered, skip selection and go directly to that method
          if (result.offer && result.offer.length === 1) {
            const method = result.offer[0];
            setSelectedMfaMethod(method);

            if (method === "passkey") {
              // Handle WebAuthn authentication directly
              handleWebAuthnAuthentication(email, password);
            } else {
              // Show the code input modal for TOTP/backup codes directly
              setMfaModalOpen(true);
            }
          } else {
            // Multiple methods available, show selection modal
            setMfaMethodSelectionOpen(true);
          }

          setLoading(false);
          return;
        }
      } catch (e) {
        // Show specific error message if available, otherwise show generic message
        setError(e.message || "Invalid credentials. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      const newPassword = newPasswordRef.current.value;
      const confirmPassword = confirmPasswordRef.current.value;
      if (!isValidInput(newPassword)) {
        dispatch(
          addToast({
            type: "error",
            message:
              "Password must be at least 8 characters long and contain at least 3 of the following: uppercase letter, lowercase letter, number, symbol",
            timer: 10,
          })
        );
      } else if (newPassword !== confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
      } else {
        try {
          setLoading(true);

          await dispatch(updatePassword(newPassword, resetToken));
          // remove reset token from url
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          // reload page
          window.location.reload();
        } catch (e) {
          setError("An error occurred. Please try again");
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleMFASubmit = async (mfaCode) => {
    if (!mfaCredentials) return;

    try {
      setLoading(true);
      await dispatch(
        login(mfaCredentials.email, mfaCredentials.password, mfaCode)
      );
      setMfaModalOpen(false);
      setMfaCredentials(null);
    } catch (e) {
      setError(e.message || "Invalid MFA code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMFAMethodSelected = (method) => {
    setSelectedMfaMethod(method);
    setMfaMethodSelectionOpen(false);

    if (method === "passkey") {
      // Handle WebAuthn authentication
      handleWebAuthnAuthentication();
    } else {
      // Show the code input modal for TOTP/backup codes
      setMfaModalOpen(true);
    }
  };

  const handleWebAuthnAuthentication = async (
    email = null,
    password = null
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Use provided credentials or fall back to state
      const userEmail = email || mfaCredentials?.email;
      const userPassword = password || mfaCredentials?.password;

      if (!userEmail || !userPassword) {
        throw new Error("Missing credentials for WebAuthn authentication");
      }

      // Step 1: Get authentication challenge from backend
      const { beginWebAuthnAuthentication } = await import("../api/auth.js");
      const challengeResponse = await beginWebAuthnAuthentication(userEmail);

      if (challengeResponse.status !== "success") {
        throw new Error(
          challengeResponse.message || "Failed to get authentication challenge"
        );
      }

      const challengeData = challengeResponse.challenge;

      // Step 2: Convert challenge and other data to ArrayBuffer format
      let challengeArrayBuffer;
      if (typeof challengeData.challenge === "string") {
        try {
          // Try base64 decoding first
          challengeArrayBuffer = Uint8Array.from(
            atob(challengeData.challenge),
            (c) => c.charCodeAt(0)
          ).buffer;
        } catch (e) {
          // If base64 fails, try treating as raw string
          challengeArrayBuffer = new TextEncoder().encode(
            challengeData.challenge
          ).buffer;
        }
      } else {
        challengeArrayBuffer = challengeData.challenge;
      }

      // Convert allowCredentials to ArrayBuffer format
      const allowCredentials = challengeData.allowCredentials?.map((cred) => ({
        ...cred,
        id:
          typeof cred.id === "string"
            ? Uint8Array.from(atob(cred.id), (c) => c.charCodeAt(0)).buffer
            : cred.id,
      }));

      const publicKeyOptions = {
        ...challengeData,
        challenge: challengeArrayBuffer,
        allowCredentials: allowCredentials,
      };

      // Step 3: Use WebAuthn API to authenticate
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });

      // Step 4: Call the same login route with username, password, and WebAuthn credential
      const { loginWithWebAuthn } = await import("../store/app/action.js");
      const result = await dispatch(
        loginWithWebAuthn(userEmail, userPassword, credential)
      );

      // Close modals
      setMfaMethodSelectionOpen(false);
      setMfaModalOpen(false);
    } catch (error) {
      // Handle specific WebAuthn errors
      if (error.name === "NotAllowedError") {
        setError(
          "Authentication was cancelled or not allowed. Please try again."
        );
      } else if (error.name === "NotSupportedError") {
        setError(
          "WebAuthn is not supported on this device. Please use a different authentication method."
        );
      } else if (error.name === "SecurityError") {
        setError("Security error occurred. Please try again.");
      } else {
        setError(
          error.message || "WebAuthn authentication failed. Please try again."
        );
      }

      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen dark:bg-theme-surfacebg-theme-surface-light flex items-center justify-center p-0 md:p-4">
      <div className="w-full max-w-none md:max-w-7xl">
        <div className="bg-white dark:bg-theme-surface-light md:rounded-2xl md:shadow-2xl overflow-hidden flex flex-col md:flex-row min-h-screen md:min-h-[600px]">
          {/* Left Side - Branding */}
          <div className="md:w-1/2 w-full md:h-auto h-1/3 relative overflow-hidden bg-gradient-to-br bg-theme-primary to-theme-button-primary-hover">
            {/* Geometric Shapes */}
            <div className="absolute inset-0">
              <div className="absolute w-64 h-64 bg-white opacity-[5%] rounded-lg transform rotate-45 -left-32 top-16"></div>
              <div className="absolute w-80 h-80 bg-white opacity-[3%] rounded-lg transform rotate-45 -right-40 top-32"></div>
              <div className="absolute w-72 h-72 bg-white opacity-[4%] rounded-lg transform rotate-45 left-8 -bottom-36"></div>
              <div className="absolute w-48 h-48 bg-white opacity-[2%] rounded-full bottom-8 right-8"></div>
              <div className="absolute w-64 h-64 bg-white opacity-[3%] rounded-full bottom-32 right-16"></div>
            </div>

            {/* Logo and Branding */}
            {isApp ? (
              <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-4 md:p-8">
                <div className="mb-4 md:mb-8">
                  <Link className="block" to="/">
                    <img
                      src="https://cdn.saucey.com/logo/wordmark-light.svg"
                      alt="Saucey Logo"
                      className="mx-auto h- w-auto md:h-7 md:w-auto mb-2"
                    />
                    <div className="text-sm font-bold">X</div>
                    <img
                      src="https://cdn.saucey.com/direct/web/saucey-direct-logo_lightbg.svg"
                      alt="Saucey Direct Logo"
                      className="mx-auto h-6 w-auto md:h-8 md:w-auto mt-2"
                    />
                  </Link>
                </div>
                <h1 className="text-2xl font-bold text-white mb-2 md:text-4xl md:mb-4">
                  Partner
                </h1>
                <p className="text-white opacity-90 text-sm md:text-lg">
                  Your comprehensive business management platform
                </p>
                {appVersion && (
                  <p className="text-white opacity-20 text-xs mt-2">
                    Version {appVersion}
                  </p>
                )}
                {config.environment !== "production" && (
                  <div className="text-white opacity-20 text-xs mt-2">
                    {config.environment}
                  </div>
                )}
              </div>
            ) : (
              <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-4 md:p-8">
                <div className="mb-4 md:mb-8">
                  <Link className="block" to="/">
                    {isDirect ? (
                      <img
                        src="https://cdn.saucey.com/direct/web/saucey-direct-logo_lightbg.svg"
                        alt="Saucey Direct Logo"
                        className="mx-auto h-6 w-auto md:h-8 md:w-auto"
                      />
                    ) : (
                      <img
                        src="https://cdn.saucey.com/logo/wordmark-light.svg"
                        alt="Saucey Logo"
                        className="mx-auto h-6 w-auto md:h-8 md:w-auto"
                      />
                    )}
                  </Link>
                </div>
                <h1 className="text-2xl font-bold text-white mb-2 md:text-4xl md:mb-4">
                  {isDirect ? "Merchant Dashboard" : "Vendor Dashboard"}
                </h1>
                <p className="text-white opacity-90 text-sm md:text-lg">
                  Your comprehensive business management platform
                </p>
              </div>
            )}
          </div>

          {/* Right Side - Login Form */}
          <div className="md:w-1/2 w-full md:h-auto h-2/3 p-6 md:p-12 flex flex-col justify-center dark:bg-theme-surface-accent">
            <div className="max-w-md mx-auto w-full">
              <h2 className="text-3xl font-bold dark:text-white text-slate-800 mb-2">
                {resetToken ? "Set New Password" : "Welcome Back"}
              </h2>
              <p className="dark:text-white text-slate-600 mb-8">
                {resetToken
                  ? "Please enter your new password below"
                  : "Sign in to your account to continue"}
              </p>

              {/* Form */}
              <form onSubmit={_login} className="space-y-6">
                {!resetToken ? (
                  <div className="space-y-4">
                    <div>
                      <label
                        className="block text-sm font-medium mb-1"
                        htmlFor="email"
                      >
                        Email Address
                      </label>
                      <input
                        disabled={loading}
                        id="email"
                        className="form-input w-full"
                        type="email"
                        placeholder="Enter your email"
                        ref={emailRef}
                      />
                    </div>
                    <div>
                      <label
                        className="block text-sm font-medium mb-1"
                        htmlFor="password"
                      >
                        Password
                      </label>
                      <input
                        disabled={loading}
                        id="password"
                        className="form-input w-full"
                        type="password"
                        placeholder="Enter your password"
                        autoComplete="on"
                        ref={passwordRef}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label
                        className="block text-sm font-medium mb-1"
                        htmlFor="new_password"
                      >
                        New Password
                      </label>
                      <input
                        disabled={loading}
                        id="new_password"
                        className="form-input w-full"
                        type="password"
                        placeholder="Enter new password"
                        ref={newPasswordRef}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </div>
                    <div>
                      <label
                        className="block text-sm font-medium mb-1"
                        htmlFor="confirm_password"
                      >
                        Confirm New Password
                      </label>
                      <input
                        disabled={loading}
                        id="confirm_password"
                        className="form-input w-full"
                        type="password"
                        placeholder="Confirm new password"
                        ref={confirmPasswordRef}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <PasswordStrengthIndicator
                        password={newPassword}
                        confirmPassword={confirmPassword}
                        className="mt-3"
                        setPasswordOk={setPasswordOk}
                      />
                    </div>
                  </div>
                )}

                {error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg">
                    {error}
                  </div>
                )}

                <PrimaryButton
                  loading={loading}
                  type="submit"
                  onClick={_login}
                  width="full"
                  disabled={resetToken ? !passwordOk : false}
                >
                  {resetToken ? "Set New Password" : "Sign In"}
                </PrimaryButton>
              </form>

              {!resetToken && (
                <div className="mt-6 space-y-4">
                  <div className="text-center">
                    <SecondaryButton
                      className="text-theme-button-primary hover:text-theme-button-primary-hover underline hover:no-underline transition-all duration-200"
                      loading={loading}
                      onClick={async (e) => {
                        e.preventDefault();
                        //check if email is empty
                        //then check if email is valid
                        if (!emailRef.current.value) {
                          dispatch(
                            addToast({
                              type: "error",
                              message: "Please enter your email address",
                              timer: 5,
                            })
                          );
                        } else if (
                          !emailRef.current.value.includes("@") ||
                          !emailRef.current.value.includes(".")
                        ) {
                          dispatch(
                            addToast({
                              type: "error",
                              message: "Please enter a valid email address",
                              timer: 5,
                            })
                          );
                        } else {
                          //dispatch forgot password
                          try {
                            setLoading(true);
                            await forgotPassword(emailRef.current.value);
                            dispatch(
                              addToast({
                                type: "success",
                                message:
                                  "We've sent you instructions to reset your password. Please check your email.",
                              })
                            );
                          } catch (e) {
                            dispatch(
                              addToast({
                                type: "error",
                                message: "An error occurred. Please try again",
                                timer: 5,
                              })
                            );
                          } finally {
                            setLoading(false);
                          }
                        }
                      }}
                    >
                      Forgot Password?
                    </SecondaryButton>
                  </div>
                  {/* <div className="text-center">
                    <p className="text-sm dark:text-white text-slate-600 mb-2">
                      Don't have an account?{" "}
                      <Link
                        to="/signup"
                        className="text-theme-button-primary hover:text-theme-button-primary-hover font-medium underline hover:no-underline transition-all duration-200"
                      >
                        Sign Up
                      </Link>
                    </p>
                  </div> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MFA Code Modal */}
      <MFACodeModal
        isOpen={mfaModalOpen}
        onClose={() => {
          setMfaModalOpen(false);
          setMfaCredentials(null);
          setMfaOffer(null);
          setSelectedMfaMethod(null);
          setError(null);
        }}
        onSubmit={handleMFASubmit}
        loading={loading}
        error={error}
        offer={mfaOffer}
      />

      {/* MFA Method Selection Modal */}
      <MFAMethodSelectionModal
        isOpen={mfaMethodSelectionOpen}
        onClose={() => {
          setMfaMethodSelectionOpen(false);
          setMfaCredentials(null);
          setMfaOffer(null);
          setSelectedMfaMethod(null);
          setError(null);
        }}
        onMethodSelected={handleMFAMethodSelected}
        offer={mfaOffer}
        loading={loading}
        error={error}
      />
    </main>
  );
}

export default Signin;
